import { useMemo } from "react";

import { useWorkspaceUsers } from "src/api/taktile/queries";
import { useCaseQueue } from "src/cases/queries/queues";
import {
  OrganizationUser,
  WorkspaceSimpleRoleType,
} from "src/clients/taktile-api";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import { useCurrentUserId, useIsFWDOrAdmin } from "src/store/AuthStore";
import { isWorkspaceAdmin } from "src/utils/user";

export const useQueueMembers = (queueId?: string) => {
  const currentUserId = useCurrentUserId();
  const { workspace, orgId } = useWorkspaceContext();
  const isFWDOrAdmin = useIsFWDOrAdmin();
  const { data: queue, isLoading: isLoadingQueue } = useCaseQueue(queueId);
  const { data: users = [], isLoading: isLoadingUsers } = useWorkspaceUsers(
    orgId,
    workspace.id,
    {
      // We want to be able to display deactivated users
      include_deactivated: true,
      // We want only users with defined workspace roles
      workspace_role_filters: [
        WorkspaceSimpleRoleType.ADMIN,
        WorkspaceSimpleRoleType.EDITOR,
        WorkspaceSimpleRoleType.REVIEWER,
      ],
    },
  );

  const queueMembers = useMemo(() => {
    return users
      .filter((user): user is OrganizationUser => !!user)
      .filter(
        (user) =>
          // Include current user if they're a workspace admin OR have FWD rights
          (user.id === currentUserId &&
            (isWorkspaceAdmin(workspace.id, user) || isFWDOrAdmin)) ||
          // Include all explicit queue members
          queue?.members.some((m) => m.user.id === user.id),
      );
  }, [users, queue, workspace.id, currentUserId, isFWDOrAdmin]);

  return {
    data: queueMembers,
    isLoading: isLoadingQueue || isLoadingUsers,
  };
};
